"use client";

import { useCallback, useEffect } from "react";
import {
  useUserPreferences,
  useUpdateUserPreferences,
} from "@/lib/user-preferences/use-user-preferences";

/**
 * Per-user, per-CRM-table view preferences (column widths, column visibility,
 * column order, and page size), persisted server-side in `app_users.user_prefs`.
 *
 * Reads come from the SHARED app-wide preferences cache (`useUserPreferences`),
 * so mounting this hook — even twice for one table (the ColumnManager reads
 * visibility/order, the SimpleTable reads width/page-size) — never triggers its
 * own network request; the whole app fetches the user's preferences at most once
 * and shares it across every page. Writes go through the shared optimistic
 * mutation (`useUpdateUserPreferences`).
 *
 * This REPLACES the old per-browser localStorage stores (the column-manager blob
 * for visibility/order, and in-memory-only width/page-size that reset on
 * reload). Server-side storage makes prefs survive logout and follow the user
 * across browsers/devices, like `savedViewHiddenByEntity` (see
 * USER_PREFERENCES.md).
 *
 * `tableKey` is the table's storage key — the same identifier the column-manager
 * uses (`storageKey || `${entityName}-column-preferences``) — so all four prefs
 * for one table share a namespace.
 *
 * On first load (server has no value for this table yet) we do a one-time
 * migration of any legacy localStorage column-manager blob up to the server,
 * then remove the key.
 */

/** Shape of the legacy per-column localStorage blob written by useColumnStorage. */
interface LegacyColumnPreference {
  visible?: boolean;
  width?: number;
  order?: number;
}
interface LegacyColumnPreferences {
  columns?: Record<string, LegacyColumnPreference>;
  columnOrder?: string[];
}

/** Table keys already migration-checked this session (dedupes the two instances). */
const migratedKeys = new Set<string>();

export interface UseTablePrefsResult {
  /** columnId → width in px. Undefined when the user has none. */
  columnWidths: Record<string, number> | undefined;
  /** columnId → enabled. Undefined when the user has none. */
  columnVisibility: Record<string, boolean> | undefined;
  /** Ordered column ids. Undefined when the user has none. */
  columnOrder: string[] | undefined;
  /** Rows per page. Undefined when the user has none. */
  pageSize: number | undefined;
  persistColumnWidths: (widths: Record<string, number>) => void;
  persistColumnVisibility: (visibility: Record<string, boolean>) => void;
  persistColumnOrder: (order: string[]) => void;
  persistPageSize: (pageSize: number) => void;
  /** True once the shared preferences cache has loaded. */
  isHydrated: boolean;
}

/** Best-effort read + parse of the legacy column-manager localStorage blob. */
function readLegacyBlob(tableKey: string): LegacyColumnPreferences | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(tableKey);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      return parsed as LegacyColumnPreferences;
    }
  } catch {
    // Malformed / unavailable storage — ignore.
  }
  return null;
}

function clearLegacyBlob(tableKey: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(tableKey);
  } catch {
    // Storage disabled — nothing to clean up.
  }
}

export const useTablePrefs = (
  tableKey: string | undefined
): UseTablePrefsResult => {
  const { preferences, isHydrated } = useUserPreferences();
  const update = useUpdateUserPreferences();

  const columnWidths = tableKey
    ? preferences.tableColumnWidthsByKey?.[tableKey]
    : undefined;
  const columnVisibility = tableKey
    ? preferences.tableColumnVisibilityByKey?.[tableKey]
    : undefined;
  const columnOrder = tableKey
    ? preferences.tableColumnOrderByKey?.[tableKey]
    : undefined;
  const pageSize = tableKey
    ? preferences.tablePageSizeByKey?.[tableKey]
    : undefined;

  // One-time migration of any legacy localStorage blob to the server. Runs once
  // per table key per session (guarded so the two hook instances for one table
  // don't both migrate), and only when the server has nothing for this table yet.
  useEffect(() => {
    if (!tableKey || !isHydrated || migratedKeys.has(tableKey)) return;
    const hasServer =
      columnWidths !== undefined ||
      columnVisibility !== undefined ||
      columnOrder !== undefined;
    migratedKeys.add(tableKey);
    if (hasServer) return;

    const legacy = readLegacyBlob(tableKey);
    if (!legacy) return;

    const legacyWidths: Record<string, number> = {};
    const legacyVisibility: Record<string, boolean> = {};
    if (legacy.columns) {
      for (const [colId, pref] of Object.entries(legacy.columns)) {
        if (typeof pref?.width === "number") legacyWidths[colId] = pref.width;
        if (typeof pref?.visible === "boolean") {
          legacyVisibility[colId] = pref.visible;
        }
      }
    }
    if (Object.keys(legacyWidths).length > 0) {
      update({ setTableColumnWidths: { tableKey, widths: legacyWidths } });
    }
    if (Object.keys(legacyVisibility).length > 0) {
      update({
        setTableColumnVisibility: { tableKey, visibility: legacyVisibility },
      });
    }
    if (legacy.columnOrder && legacy.columnOrder.length > 0) {
      update({ setTableColumnOrder: { tableKey, order: legacy.columnOrder } });
    }
    clearLegacyBlob(tableKey);
  }, [tableKey, isHydrated, columnWidths, columnVisibility, columnOrder, update]);

  const persistColumnWidths = useCallback(
    (widths: Record<string, number>) => {
      if (tableKey) update({ setTableColumnWidths: { tableKey, widths } });
    },
    [tableKey, update]
  );

  const persistColumnVisibility = useCallback(
    (visibility: Record<string, boolean>) => {
      if (tableKey) {
        update({ setTableColumnVisibility: { tableKey, visibility } });
      }
    },
    [tableKey, update]
  );

  const persistColumnOrder = useCallback(
    (order: string[]) => {
      if (tableKey) update({ setTableColumnOrder: { tableKey, order } });
    },
    [tableKey, update]
  );

  const persistPageSize = useCallback(
    (next: number) => {
      if (tableKey) update({ setTablePageSize: { tableKey, pageSize: next } });
    },
    [tableKey, update]
  );

  return {
    columnWidths,
    columnVisibility,
    columnOrder,
    pageSize,
    persistColumnWidths,
    persistColumnVisibility,
    persistColumnOrder,
    persistPageSize,
    isHydrated,
  };
};
